import { useParams } from "@/router";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { AuditLogTable } from "@/pages/sections/[sectionId]/audit-log/_components";
import { PERSON_AUDIT_LOG_QUERY } from "./queries";
import { useQuery } from "@apollo/client";
import { useLayoutEffect, useRef } from "react";
import usePagination from "@/hooks/usePagination";
import { useParams as useSearchParams } from "@/hooks/useSearchParams";
import { TableControls, TableFooter, TableHeading } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";

const path = "/people/:pgaId/person/change-history";

/**
 * Remounted via `key={pgaId}` so in-memory pagination state starts clean when switching people.
 * An effect-based reset would still let one query fire with the prior after/before cursor.
 */
function ChangeHistoryContent({ pgaId }: { pgaId: string }) {
  const { createHeaderPagination, createFooterPagination, paginationVariables } = usePagination({
    defaultPageSize: 50
  });

  const { data, loading, error } = useQuery(PERSON_AUDIT_LOG_QUERY, {
    variables: {
      pgaId,
      ...paginationVariables
    }
  });

  if (error) {
    return (
      <ErrorBoundary variant="inline" title="Failed to Load Change History" message={error.message} className="mt-12" />
    );
  }

  const { auditLog } = data?.person || {};

  return (
    <>
      <TableHeading
        heading="Change History"
        body="A history of all data changes associated with this person can be found below."
      />
      <AuditLogTable
        loading={loading}
        data={auditLog?.nodes || []}
        columns={["description", "changedAt", "whodunnit", "changeset"]}
        hasActiveFilters={false}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Results Found" caption="There is no change history for this person." iconName="today" />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls pagination={createHeaderPagination(auditLog, { loading })} onSortClick={onSortClick} />
        )}
        renderFooter={() => <TableFooter pagination={createFooterPagination(auditLog, { loading })} />}
      />
    </>
  );
}

export default function ChangeHistory() {
  useRequireCapability({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPeople });
  const { pgaId } = useParams(path);
  const { searchParams, deleteSearchParams } = useSearchParams();
  // URL pagination params belong to the last person whose Change History we rendered.
  // Remount alone does not clear them; strip before mounting content when switching people.
  // Cold load / shared deep links keep after/before/currentPage so usePagination can seed.
  const paginationOwnerPgaIdRef = useRef<string | null>(null);

  const hasPaginationParams =
    searchParams.has("after") || searchParams.has("before") || searchParams.has("currentPage");
  // Only strip URL pagination when switching people. A null owner ref is a cold load /
  // shared deep link for this pgaId — let usePagination seed from after/before/currentPage.
  const needsUrlPaginationReset =
    hasPaginationParams && paginationOwnerPgaIdRef.current !== null && paginationOwnerPgaIdRef.current !== pgaId;

  useLayoutEffect(() => {
    if (needsUrlPaginationReset) {
      deleteSearchParams(["currentPage", "after", "before"]);
      return;
    }
    paginationOwnerPgaIdRef.current = pgaId;
  }, [pgaId, needsUrlPaginationReset, deleteSearchParams]);

  if (needsUrlPaginationReset) {
    return null;
  }

  return <ChangeHistoryContent key={pgaId} pgaId={pgaId} />;
}
